import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  CallMissed as CallMissedIcon,
  Schedule as ScheduleIcon,
  GetApp as GetAppIcon,
  Publish as PublishIcon,
  FileCopy as FileCopyIcon,
  VolumeUp as VolumeUpIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Timer as TimerIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format, startOfDay, endOfDay, subDays, subWeeks, subMonths } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

import { AuthContext } from '../../context/Auth/AuthContext';
import { AsteriskContext } from '../../context/Asterisk/AsteriskContext';
import { i18n } from '../../translate/i18n';
import toastError from '../../errors/toastError';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: '100%',
    margin: '0 auto'
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statsGrid: {
    marginBottom: theme.spacing(3)
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
    border: `1px solid ${theme.palette.primary.main}20`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4]
    }
  },
  statsContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  statsIcon: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statsText: {
    flexGrow: 1
  },
  statsValue: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  statsLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  statsChange: {
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  contentCard: {
    marginBottom: theme.spacing(2)
  },
  tableContainer: {
    maxHeight: '60vh',
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 'bold',
        borderBottom: `2px solid ${theme.palette.divider}`
      }
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light + '20'
    }
  },
  callTypeChip: {
    minWidth: 80,
    '&.inbound': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    '&.outbound': {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText
    },
    '&.missed': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    '&.internal': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText
    }
  },
  statusChip: {
    minWidth: 80,
    '&.answered': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    '&.busy': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText
    },
    '&.failed': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    '&.no-answer': {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.grey[50]
    }
  },
  filterSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  filterGrid: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  callDetails: {
    padding: theme.spacing(2)
  },
  callDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  callDetailLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary
  },
  callDetailValue: {
    color: theme.palette.text.primary
  },
  audioPlayer: {
    width: '100%',
    marginTop: theme.spacing(2)
  },
  durationDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  quickFilters: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  quickFilter: {
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  },
  bulkActions: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

const AsteriskCallHistory = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { callHistory: contextCallHistory, getCallHistory, deleteCallHistory, exportCallHistory, connections } = useContext(AsteriskContext);

  // Estado principal
  const [callHistory, setCallHistory] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    inbound: 0,
    outbound: 0,
    missed: 0,
    answered: 0,
    averageDuration: 0,
    totalDuration: 0,
    todayTotal: 0,
    todayChange: 0
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Estados da tabela
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCalls, setSelectedCalls] = useState([]);

  // Estados de filtros
  const [filterDateRange, setFilterDateRange] = useState('today');
  const [filterCallType, setFilterCallType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEndpoint, setFilterEndpoint] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  // Estados de modais
  const [callDetailsOpen, setCallDetailsOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Estados de reprodutor de áudio
  const [audioPlaying, setAudioPlaying] = useState({});

  // Carregar dados iniciais
  useEffect(() => {
    loadCallHistory();
  }, [filterDateRange, customDateStart, customDateEnd]);

  // Atualizar lista quando contexto mudar
  useEffect(() => {
    if (contextCallHistory) {
      setCallHistory(contextCallHistory);
      updateStats(contextCallHistory);
    }
  }, [contextCallHistory]);

  const loadCallHistory = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange();
      await getCallHistory({
        startDate: dateRange.start,
        endDate: dateRange.end,
        ...getFilterParams()
      });
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadCallHistory();
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (filterDateRange) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'yesterday':
        start = startOfDay(subDays(now, 1));
        end = endOfDay(subDays(now, 1));
        break;
      case 'week':
        start = startOfDay(subWeeks(now, 1));
        end = endOfDay(now);
        break;
      case 'month':
        start = startOfDay(subMonths(now, 1));
        end = endOfDay(now);
        break;
      case 'custom':
        start = customDateStart ? new Date(customDateStart) : startOfDay(subDays(now, 7));
        end = customDateEnd ? new Date(customDateEnd) : endOfDay(now);
        break;
      default:
        start = startOfDay(now);
        end = endOfDay(now);
    }

    return { start, end };
  };

  const getFilterParams = () => {
    return {
      callType: filterCallType !== 'all' ? filterCallType : undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      endpointId: filterEndpoint !== 'all' ? filterEndpoint : undefined,
      userId: filterUser !== 'all' ? filterUser : undefined,
      search: searchTerm || undefined
    };
  };

  const updateStats = (callList) => {
    const total = callList.length;
    const inbound = callList.filter(c => c.direction === 'inbound').length;
    const outbound = callList.filter(c => c.direction === 'outbound').length;
    const missed = callList.filter(c => c.status === 'no-answer' || c.status === 'missed').length;
    const answered = callList.filter(c => c.status === 'answered').length;

    const totalDuration = callList.reduce((sum, call) => sum + (call.duration || 0), 0);
    const averageDuration = answered > 0 ? totalDuration / answered : 0;

    // Calcular estatísticas de hoje
    const today = startOfDay(new Date());
    const todayCalls = callList.filter(c => new Date(c.createdAt) >= today);
    const todayTotal = todayCalls.length;

    // Calcular mudança comparada a ontem
    const yesterday = startOfDay(subDays(new Date(), 1));
    const yesterdayCalls = callList.filter(c => {
      const callDate = new Date(c.createdAt);
      return callDate >= yesterday && callDate < today;
    });
    const yesterdayTotal = yesterdayCalls.length;
    const todayChange = yesterdayTotal > 0 ? ((todayTotal - yesterdayTotal) / yesterdayTotal) * 100 : 0;

    setStats({
      total,
      inbound,
      outbound,
      missed,
      answered,
      averageDuration,
      totalDuration,
      todayTotal,
      todayChange
    });
  };

  const getFilteredCalls = () => {
    let filtered = [...callHistory];

    // Filtro por busca
    if (searchTerm) {
      filtered = filtered.filter(call =>
        call.callerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.calleeNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.callerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.calleeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.endpoint?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por tipo de chamada
    if (filterCallType !== 'all') {
      filtered = filtered.filter(call => call.direction === filterCallType);
    }

    // Filtro por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(call => call.status === filterStatus);
    }

    // Filtro por endpoint
    if (filterEndpoint !== 'all') {
      filtered = filtered.filter(call => call.endpointId === parseInt(filterEndpoint));
    }

    // Filtro por usuário
    if (filterUser !== 'all') {
      filtered = filtered.filter(call => call.userId === parseInt(filterUser));
    }

    // Ordenação
    filtered.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      if (sortBy === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleCallDelete = async () => {
    try {
      setLoading(true);
      if (selectedCalls.length > 0) {
        // Exclusão em lote
        for (const callId of selectedCalls) {
          await deleteCallHistory(callId);
        }
        setSelectedCalls([]);
      } else if (selectedCall) {
        // Exclusão individual
        await deleteCallHistory(selectedCall.id);
      }
      setDeleteDialogOpen(false);
      setSelectedCall(null);
      await loadCallHistory();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      setLoading(true);
      const filtered = getFilteredCalls();
      const dateRange = getDateRange();

      await exportCallHistory({
        calls: filtered.map(c => c.id),
        format,
        startDate: dateRange.start,
        endDate: dateRange.end,
        ...getFilterParams()
      });

      setExportDialogOpen(false);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAudioPlay = (callId, audioUrl) => {
    // Parar todos os outros áudios
    Object.keys(audioPlaying).forEach(id => {
      if (id !== callId && audioPlaying[id]) {
        audioPlaying[id].pause();
      }
    });

    const audio = audioPlaying[callId] || new Audio(audioUrl);

    if (!audioPlaying[callId]) {
      setAudioPlaying(prev => ({ ...prev, [callId]: audio }));
    }

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const handleMenuOpen = (event, call) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCall(call);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCall(null);
  };

  const handleSelectionChange = (callId) => {
    setSelectedCalls(prev => {
      if (prev.includes(callId)) {
        return prev.filter(id => id !== callId);
      } else {
        return [...prev, callId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredCalls = getFilteredCalls();
    if (selectedCalls.length === filteredCalls.length) {
      setSelectedCalls([]);
    } else {
      setSelectedCalls(filteredCalls.map(c => c.id));
    }
  };

  const getCallTypeIcon = (direction, status) => {
    if (status === 'missed' || status === 'no-answer') {
      return <CallMissedIcon style={{ color: '#f44336' }} />;
    }

    switch (direction) {
      case 'inbound':
        return <CallReceivedIcon style={{ color: '#4caf50' }} />;
      case 'outbound':
        return <CallMadeIcon style={{ color: '#2196f3' }} />;
      default:
        return <PhoneIcon style={{ color: '#9e9e9e' }} />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const locale = i18n.language === 'pt' ? ptBR : i18n.language === 'es' ? es : enUS;
    return format(date, 'dd/MM/yyyy HH:mm:ss', { locale });
  };

  const quickFilters = [
    { key: 'today', label: i18n.t('asterisk.callHistory.filters.today') },
    { key: 'yesterday', label: i18n.t('asterisk.callHistory.filters.yesterday') },
    { key: 'week', label: i18n.t('asterisk.callHistory.filters.week') },
    { key: 'month', label: i18n.t('asterisk.callHistory.filters.month') },
    { key: 'custom', label: i18n.t('asterisk.callHistory.filters.custom') }
  ];

  const filteredCalls = getFilteredCalls();
  const paginatedCalls = filteredCalls.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Verificar se há conexões Asterisk configuradas
  const hasConnections = connections && connections.length > 0;

  // Empty state quando não há conexões
  if (!loading && !hasConnections) {
    return (
      <Box className={classes.root}>
        <Box className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>
            <HistoryIcon />
            {i18n.t('asterisk.callHistory.title')}
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="50vh"
              textAlign="center"
              p={4}
            >
              <PhoneDisabledIcon
                style={{
                  fontSize: 80,
                  color: '#bdbdbd',
                  marginBottom: 16
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                style={{ fontWeight: 'bold', color: '#424242' }}
              >
                {i18n.t('asterisk.callHistory.emptyState.title')}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                paragraph
                style={{ maxWidth: 500 }}
              >
                {i18n.t('asterisk.callHistory.emptyState.description')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PhoneIcon />}
                onClick={() => window.location.href = '/asterisk/connections'}
                style={{ marginTop: 16 }}
              >
                {i18n.t('asterisk.callHistory.emptyState.action')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Cabeçalho da página */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          <HistoryIcon />
          {i18n.t('asterisk.callHistory.title')}
        </Typography>
        <Box className={classes.actionButtons}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? i18n.t('common.refreshing') : i18n.t('common.refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            {i18n.t('asterisk.callHistory.export')}
          </Button>
        </Box>
      </Box>

      {/* Cards de estatísticas */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#2196f3' + '20' }}>
                <HistoryIcon style={{ color: '#2196f3' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.total}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.callHistory.stats.totalCalls')}
                </Typography>
                <Box className={classes.statsChange}>
                  {stats.todayChange >= 0 ? (
                    <TrendingUpIcon style={{ color: '#4caf50', fontSize: 16 }} />
                  ) : (
                    <TrendingDownIcon style={{ color: '#f44336', fontSize: 16 }} />
                  )}
                  <Typography
                    variant="caption"
                    style={{ color: stats.todayChange >= 0 ? '#4caf50' : '#f44336' }}
                  >
                    {Math.abs(stats.todayChange).toFixed(1)}% {i18n.t('asterisk.callHistory.stats.today')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#4caf50' + '20' }}>
                <CallReceivedIcon style={{ color: '#4caf50' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.inbound}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.callHistory.stats.inbound')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {stats.total > 0 ? ((stats.inbound / stats.total) * 100).toFixed(1) : 0}% {i18n.t('asterisk.callHistory.stats.ofTotal')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#f44336' + '20' }}>
                <CallMissedIcon style={{ color: '#f44336' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.missed}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.callHistory.stats.missed')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {stats.total > 0 ? ((stats.missed / stats.total) * 100).toFixed(1) : 0}% {i18n.t('asterisk.callHistory.stats.ofTotal')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#ff9800' + '20' }}>
                <AccessTimeIcon style={{ color: '#ff9800' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>
                  {formatDuration(Math.round(stats.averageDuration))}
                </Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.callHistory.stats.avgDuration')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDuration(stats.totalDuration)} {i18n.t('asterisk.callHistory.stats.totalTime')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Card principal */}
      <Card className={classes.contentCard}>
        {/* Filtros rápidos */}
        <Box className={classes.filterSection}>
          <Box className={classes.quickFilters}>
            {quickFilters.map((filter) => (
              <Button
                key={filter.key}
                variant={filterDateRange === filter.key ? "contained" : "outlined"}
                size="small"
                className={`${classes.quickFilter} ${filterDateRange === filter.key ? 'active' : ''}`}
                onClick={() => setFilterDateRange(filter.key)}
              >
                {filter.label}
              </Button>
            ))}
          </Box>

          {/* Filtros personalizados */}
          <Grid container spacing={2} className={classes.filterGrid}>
            {filterDateRange === 'custom' && (
              <>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label={i18n.t('asterisk.callHistory.startDate')}
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label={i18n.t('asterisk.callHistory.endDate')}
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={i18n.t('asterisk.callHistory.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.callHistory.direction')}</InputLabel>
                <Select
                  value={filterCallType}
                  onChange={(e) => setFilterCallType(e.target.value)}
                  label={i18n.t('asterisk.callHistory.direction')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  <MenuItem value="inbound">{i18n.t('asterisk.callHistory.inbound')}</MenuItem>
                  <MenuItem value="outbound">{i18n.t('asterisk.callHistory.outbound')}</MenuItem>
                  <MenuItem value="internal">{i18n.t('asterisk.callHistory.internal')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.callHistory.status')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={i18n.t('asterisk.callHistory.status')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  <MenuItem value="answered">{i18n.t('asterisk.callHistory.answered')}</MenuItem>
                  <MenuItem value="busy">{i18n.t('asterisk.callHistory.busy')}</MenuItem>
                  <MenuItem value="no-answer">{i18n.t('asterisk.callHistory.noAnswer')}</MenuItem>
                  <MenuItem value="failed">{i18n.t('asterisk.callHistory.failed')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Ações em lote */}
        {selectedCalls.length > 0 && (
          <Box className={classes.bulkActions}>
            <Typography variant="body2">
              {i18n.t('asterisk.callHistory.selectedCount', { count: selectedCalls.length })}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => setExportDialogOpen(true)}
                size="small"
                style={{ marginRight: 8 }}
              >
                {i18n.t('common.export')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                size="small"
              >
                {i18n.t('common.delete')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Progress bar para refresh */}
        {refreshing && <LinearProgress />}

        {/* Tabela */}
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Switch
                    checked={selectedCalls.length === filteredCalls.length && filteredCalls.length > 0}
                    indeterminate={selectedCalls.length > 0 && selectedCalls.length < filteredCalls.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.dateTime')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.direction')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.caller')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.callee')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.duration')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.status')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.endpoint')}</TableCell>
                <TableCell>{i18n.t('asterisk.callHistory.recording')}</TableCell>
                <TableCell align="center">{i18n.t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCalls.map((call) => (
                <TableRow
                  key={call.id}
                  className={`${classes.tableRow} ${selectedCalls.includes(call.id) ? 'selected' : ''}`}
                >
                  <TableCell padding="checkbox">
                    <Switch
                      checked={selectedCalls.includes(call.id)}
                      onChange={() => handleSelectionChange(call.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(call.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getCallTypeIcon(call.direction, call.status)}
                      <Chip
                        label={i18n.t(`asterisk.callHistory.${call.direction}`)}
                        size="small"
                        className={`${classes.callTypeChip} ${call.direction}`}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {call.callerName || call.callerNumber}
                      </Typography>
                      {call.callerName && (
                        <Typography variant="caption" color="textSecondary">
                          {call.callerNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {call.calleeName || call.calleeNumber}
                      </Typography>
                      {call.calleeName && (
                        <Typography variant="caption" color="textSecondary">
                          {call.calleeNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.durationDisplay}>
                      <TimerIcon style={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {formatDuration(call.duration)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={i18n.t(`asterisk.callHistory.${call.status}`)}
                      size="small"
                      className={`${classes.statusChip} ${call.status}`}
                    />
                  </TableCell>
                  <TableCell>
                    {call.endpoint ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneIcon style={{ fontSize: 16 }} />
                        <Typography variant="body2">
                          {call.endpoint.name}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t('asterisk.callHistory.noEndpoint')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {call.recordingUrl ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title={i18n.t('asterisk.callHistory.playRecording')}>
                          <IconButton
                            size="small"
                            onClick={() => handleAudioPlay(call.id, call.recordingUrl)}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t('asterisk.callHistory.downloadRecording')}>
                          <IconButton
                            size="small"
                            onClick={() => window.open(call.recordingUrl, '_blank')}
                          >
                            <GetAppIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t('asterisk.callHistory.noRecording')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={i18n.t('asterisk.callHistory.details')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedCall(call);
                            setCallDetailsOpen(true);
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t('common.more')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, call)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {paginatedCalls.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm || filterCallType !== 'all' || filterStatus !== 'all'
                        ? i18n.t('asterisk.callHistory.noFilterResults')
                        : i18n.t('asterisk.callHistory.noCalls')
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={filteredCalls.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={i18n.t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            i18n.t('common.displayedRows', { from, to, count })
          }
        />
      </Card>

      {/* Menu de contexto */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          setCallDetailsOpen(true);
        }}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          {i18n.t('asterisk.callHistory.details')}
        </MenuItem>
        {selectedCall?.recordingUrl && (
          <MenuItem onClick={() => {
            handleMenuClose();
            window.open(selectedCall.recordingUrl, '_blank');
          }}>
            <ListItemIcon>
              <GetAppIcon />
            </ListItemIcon>
            {i18n.t('asterisk.callHistory.downloadRecording')}
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleMenuClose();
          // Implementar callback
        }}>
          <ListItemIcon>
            <PhoneIcon />
          </ListItemIcon>
          {i18n.t('asterisk.callHistory.callback')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleMenuClose();
          setDeleteDialogOpen(true);
        }} style={{ color: '#f44336' }}>
          <ListItemIcon>
            <DeleteIcon style={{ color: '#f44336' }} />
          </ListItemIcon>
          {i18n.t('common.delete')}
        </MenuItem>
      </Menu>

      {/* Dialog detalhes da chamada */}
      <Dialog
        open={callDetailsOpen}
        onClose={() => {
          setCallDetailsOpen(false);
          setSelectedCall(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{i18n.t('asterisk.callHistory.callDetails')}</DialogTitle>
        <DialogContent>
          {selectedCall && (
            <Box className={classes.callDetails}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    {i18n.t('asterisk.callHistory.basicInfo')}
                  </Typography>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.callId')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {selectedCall.callId}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.dateTime')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {formatDate(selectedCall.createdAt)}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.direction')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {i18n.t(`asterisk.callHistory.${selectedCall.direction}`)}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.status')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {i18n.t(`asterisk.callHistory.${selectedCall.status}`)}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.duration')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {formatDuration(selectedCall.duration)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    {i18n.t('asterisk.callHistory.participants')}
                  </Typography>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.caller')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {selectedCall.callerName || selectedCall.callerNumber}
                      {selectedCall.callerName && (
                        <Typography variant="caption" display="block">
                          {selectedCall.callerNumber}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.callee')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {selectedCall.calleeName || selectedCall.calleeNumber}
                      {selectedCall.calleeName && (
                        <Typography variant="caption" display="block">
                          {selectedCall.calleeNumber}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.endpoint')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {selectedCall.endpoint?.name || i18n.t('asterisk.callHistory.noEndpoint')}
                    </Typography>
                  </Box>
                  <Box className={classes.callDetailRow}>
                    <Typography className={classes.callDetailLabel}>
                      {i18n.t('asterisk.callHistory.user')}:
                    </Typography>
                    <Typography className={classes.callDetailValue}>
                      {selectedCall.user?.name || i18n.t('asterisk.callHistory.noUser')}
                    </Typography>
                  </Box>
                </Grid>

                {selectedCall.recordingUrl && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {i18n.t('asterisk.callHistory.recording')}
                    </Typography>
                    <audio
                      controls
                      className={classes.audioPlayer}
                      src={selectedCall.recordingUrl}
                    >
                      {i18n.t('asterisk.callHistory.audioNotSupported')}
                    </audio>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCallDetailsOpen(false);
            setSelectedCall(null);
          }}>
            {i18n.t('common.close')}
          </Button>
          {selectedCall?.recordingUrl && (
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={() => window.open(selectedCall.recordingUrl, '_blank')}
            >
              {i18n.t('asterisk.callHistory.downloadRecording')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {selectedCalls.length > 0
            ? i18n.t('asterisk.callHistory.deleteMultipleTitle')
            : i18n.t('asterisk.callHistory.deleteTitle')
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedCalls.length > 0
              ? i18n.t('asterisk.callHistory.deleteMultipleConfirm', { count: selectedCalls.length })
              : i18n.t('asterisk.callHistory.deleteConfirm')
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={handleCallDelete}
            color="secondary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : i18n.t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de exportação */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      >
        <DialogTitle>{i18n.t('asterisk.callHistory.exportTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {i18n.t('asterisk.callHistory.exportDescription')}
          </Typography>
          <Box display="flex" gap={2} mt={2}>
            <Button
              variant="outlined"
              onClick={() => handleExport('csv')}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleExport('xlsx')}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleExport('pdf')}
            >
              PDF
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>
            {i18n.t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overlay de loading */}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default AsteriskCallHistory;